import React from 'react';

import './offer-card-details.css';
import { Button } from '@wwe-portal/ui/components/button/button';
import { ClickableDiv } from '@wwe-portal/ui/components/button/clickable-div';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons/icomoon';

import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import { useIsAuthenticated } from '@wwe-portal/web/providers/stores/auth-store';
import { useGetCurrentUser } from '@wwe-portal/web/providers/queries/user-info';
import { formatThousands } from '@wwe-portal/utils/format';
import { cn } from '@wwe-portal/ui/lib/lib';
import { TimeCountDown } from '@wwe-portal/ui/features/offer/component/time-countdown';
import TooltipWrapper from '@wwe-portal/ui/features/club-store/milestone/tooltip';
import LoadAnimateButton from '../../load-animate-button';
import { sendOfferClickEvent } from '@wwe-portal/web/libs/analytics/offer';
import { OfferPrice } from '@wwe-portal/web/utils/offer/mapper';
import { DateValue } from '@wwe-portal/ui/lib/hooks';

const leftLimit = 1000;

type OfferCardDetailsType = {
  name: string;
  price: OfferPrice;
  quantity?: number;
  endDate: DateValue;
  onHandlePurchase: any;
  transactionStarted?: string | number;
  offerId: any;
  availability: string;
  setShow: (val: boolean) => void;
  offerType: string;
  isActiveDay?: boolean;
  isInFuture: boolean;
};

function OfferCardDetails({
  name,
  price,
  quantity,
  endDate,
  onHandlePurchase,
  transactionStarted,
  offerId,
  availability,
  setShow,
  offerType,
  isActiveDay,
  isInFuture,
}: OfferCardDetailsType) {
  const t = useTranslations();
  /**
   * Use location react-router
   * Getting pathname
   */
  const pathname = usePathname();

  /**
   * Use window size custom hook
   */
  const { isTablet, isUltra, isMobile } = useResponsive();

  // const { viewInterstitial } = useTriggerSubscriptionInterstitial();

  const isDaily = offerType === 'daily';
  const isReward = offerType === 'rewards';
  const isTournament = offerType === 'tournament';

  /**
   * Use Select hook
   * Using react-redux
   *
   * Get Global -> isAuthenticated
   * Get Global -> currentUser
   */
  const isAuthenticated = useIsAuthenticated();
  const currentUser = useGetCurrentUser();
  const renderButton = () => {
    const isWithTooltip =
      transactionStarted !== offerId &&
      (!quantity || isInFuture || !isActiveDay) &&
      isDaily &&
      isAuthenticated &&
      currentUser;

    return isWithTooltip ? (
      <TooltipWrapper
        content={
          <div style={{ maxWidth: '200px' }}>
            Arriving for purchase on {availability}{' '}
          </div>
        }
      >
        <Button
          disabled
          data-id="shop_item_daily_button"
          onClick={transactionStarted ? null : onHandlePurchase}
          variant="orange"
          className={`shop-offer-card__actions__button mx-0 flex items-center justify-center`}
        >
          {transactionStarted === offerId ? (
            <div>
              <LoadAnimateButton />
            </div>
          ) : (
            <p className={`m-0 p-[5px] text-xs`}>{t('coming_soon')}</p>
          )}
        </Button>
      </TooltipWrapper>
    ) : (
      <Button
        disabled={
          (!quantity || (!isActiveDay && isDaily) || isInFuture) &&
          isAuthenticated &&
          !!currentUser
        }
        data-id={
          isReward || (isTournament && !price.priceNum)
            ? !quantity || isInFuture || (!isActiveDay && isDaily)
              ? 'shop_item_claimed_button'
              : 'shop_item_claim_button'
            : isDaily
              ? 'shop_item_daily_button'
              : 'shop_item_standart_button'
        }
        onClick={transactionStarted ? null : onHandlePurchase}
        variant="orange"
        className={`shop-offer-card__actions__button mx-0 mb-0 flex items-center justify-center`}
      >
        {transactionStarted === offerId ? (
          <div>
            <LoadAnimateButton />
          </div>
        ) : (!quantity || isInFuture || (!isActiveDay && isDaily)) &&
          isAuthenticated &&
          currentUser ? (
          <p className={`m-0 p-[5px] text-xs`}>
            {isInFuture || (!isActiveDay && isDaily)
              ? t('not_ready')
              : isReward || (isTournament && !price.priceNum)
                ? t('claimed')
                : t('purchased')}
          </p>
        ) : (
          <>{price.price}</>
        )}
      </Button>
    );
  };

  return (
    <>
      <div
        data-id="offer-detail-name"
        className="shop-offer-card__title text-xs font-normal"
      >
        {name}
      </div>
      {availability && (
        <div
          className={`shop-offer-card__available text-text500 mt-[5px] flex items-center text-xs`}
        >
          <span>{t('available')}</span>
          <span className="mb-0 pl-[5px]">{availability}</span>
        </div>
      )}
      <div
        className={cn('shop-offer-card__actions flex justify-between pt-5', {
          'pb-5': !isDaily,
        })}
      >
        {renderButton()}
        <div
          className={cn(
            'grow',
            (isTablet || isUltra) && pathname === '/shop' ? 'px-5' : 'px-[10px]'
          )}
        >
          <div
            data-id="left-count"
            id={`left-count-${offerId}`}
            className={`shop-offer-card__actions__badge text-text600 h-full rounded text-xs`}
          >
            {quantity === undefined || quantity >= leftLimit ? (
              <IcoMoonIcon iconName="infinity-fill" className="mr-1 size-4" />
            ) : (
              formatThousands(quantity)
            )}{' '}
            {t('left')}
          </div>
        </div>
        <ClickableDiv
          className="grow"
          onClick={() => {
            setShow(true);
            sendOfferClickEvent(name);
          }}
        >
          <div
            className="shop-offer-card__actions__badge shop-offer-card__actions__badge__info text-text600 h-full flex-grow rounded text-xs"
            id={`shop_card_${offerId}`}
          >
            <IcoMoonIcon className="size-4" iconName="information-fill" />
            {!isMobile && pathname === '/shop' && t('info')}
          </div>
        </ClickableDiv>
      </div>
      {!isDaily && (
        <div data-id="offer-detail-progress-date">
          <TimeCountDown
            endDate={endDate}
            className="text-text700 text-sm font-semibold"
          />
        </div>
      )}
    </>
  );
}

export { OfferCardDetails };
